import { motion } from "framer-motion";

import type { ReportIndexEntry } from "@/lib/api";
import { cn } from "@/lib/formatters";

interface WeeksListProps {
  weeks: ReportIndexEntry[];
  selected: string;
  onSelect: (week: string) => void;
}

function fmtWeekDate(iso: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export function WeeksList({ weeks, selected, onSelect }: WeeksListProps) {
  return (
    <aside className="surface p-2 h-fit lg:sticky lg:top-4 max-h-[85vh] overflow-y-auto">
      <div className="px-3 py-2 border-b border-border flex items-center justify-between">
        <p className="path-chip">weeks</p>
        <span className="font-mono text-xs text-muted">{weeks.length}</span>
      </div>
      <ul className="mt-1 space-y-0.5">
        {weeks.map((w, i) => {
          const active = w.week === selected;
          const clicksDelta = w.deltas?.clicks_pct ?? 0;
          const positive = clicksDelta > 0;
          const flat = clicksDelta === 0;
          return (
            <motion.li
              key={w.week}
              initial={{ opacity: 0, x: -6 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: Math.min(i * 0.02, 0.25), duration: 0.2 }}
            >
              <button
                type="button"
                onClick={() => onSelect(w.week)}
                className={cn(
                  "w-full text-left px-3 py-2 rounded-sm font-mono text-xs transition-all border-l-2",
                  active
                    ? "bg-gradient-to-r from-primary/10 to-transparent border-primary text-text shadow-[inset_0_0_20px_rgba(0,212,255,0.08)]"
                    : "border-transparent text-muted hover:text-text hover:bg-violet/5 hover:border-violet/40",
                )}
              >
                <div className="flex items-center justify-between">
                  <span className={cn("tracking-wider", active ? "text-primary" : "text-text/80")}>
                    {w.week}
                  </span>
                  <span className={cn(
                    "text-[10px]",
                    flat ? "text-muted" : positive ? "text-success" : "text-danger",
                  )}>
                    {flat ? "= 0" : `${positive ? "▲" : "▼"} ${Math.abs(clicksDelta).toFixed(0)}%`}
                  </span>
                </div>
                <div className="flex items-center justify-between mt-0.5">
                  <span className="text-[10px] text-muted/70">{fmtWeekDate(w.generated_at)}</span>
                  <span className="text-[10px] text-muted/70">
                    {w.summary.clicks} clk · {w.action_count} act
                  </span>
                </div>
              </button>
            </motion.li>
          );
        })}
      </ul>
    </aside>
  );
}
